/**
 * Error Toast Utility
 *
 * Display error toasts with automatic translation and "View Details" action.
 * Can be called from anywhere (hooks, components, utilities).
 */

import { toast, type ExternalToast } from "sonner";
import i18n from "i18next";
import { translateError, translateErrorTitle } from "./translate-error";
import { useErrorModalStore } from "./error-modal-store";
import type { ApiError } from "./types";

export interface ErrorToastOptions extends ExternalToast {
  /** Override the toast title (defaults to translated error title) */
  title?: string;
  /** Override the toast description (defaults to translated error message) */
  description?: string;
  /** Show "View Details" button to open error modal */
  showDetailsButton?: boolean;
}

/**
 * Display an error toast with automatic translation.
 * Shows a "View Details" button that opens a modal with comprehensive error info.
 *
 * @example
 * ```ts
 * // Basic usage
 * showErrorToast(error)
 *
 * // With custom options
 * showErrorToast(error, {
 *   title: "Upload failed",
 *   description: "Please try again",
 *   showDetailsButton: false,
 * })
 * ```
 */
export function showErrorToast(
  error: ApiError | Error | unknown,
  options: ErrorToastOptions = {},
): string | number {
  const {
    title,
    description,
    showDetailsButton = true,
    ...toastOptions
  } = options;

  // Use translated error messages
  const errorTitle = title ?? translateErrorTitle(error);
  const errorDescription = description ?? translateError(error);

  // Translate "View Details" button label
  const viewDetailsLabel = i18n.t("errors.viewDetails", {
    defaultValue: "View Details",
  });

  // Build toast options
  const finalOptions: ExternalToast = {
    ...toastOptions,
    description: errorDescription,
    richColors: true,
  };

  // Add "View Details" action if enabled
  if (showDetailsButton && error instanceof Error) {
    finalOptions.action = {
      label: viewDetailsLabel,
      onClick: () => {
        useErrorModalStore.getState().openErrorModal(error);
      },
    };
  }

  return toast.error(errorTitle, finalOptions);
}

/**
 * Display a success toast.
 *
 * @example
 * ```ts
 * showSuccessToast("Document saved")
 * showSuccessToast("Document saved", { description: "Changes have been saved" })
 * ```
 */
export function showSuccessToast(
  message: string,
  options?: ExternalToast,
): string | number {
  return toast.success(message, {
    ...options,
    richColors: true,
  });
}

/**
 * Display a loading toast that can be updated later.
 *
 * @example
 * ```ts
 * const toastId = showLoadingToast("Uploading document...")
 * // Later:
 * toast.success("Upload complete!", { id: toastId })
 * // Or on error:
 * toast.error("Upload failed", { id: toastId })
 * ```
 */
export function showLoadingToast(
  message: string,
  options?: ExternalToast,
): string | number {
  return toast.loading(message, options);
}

/**
 * Dismiss a toast by ID.
 */
export function dismissToast(toastId?: string | number): void {
  toast.dismiss(toastId);
}
